/**
 * A short list typed one item at a time — the acceptance criteria, the markets and the runs of a
 * report (ADR 9071). An input and «Add», then the items with a «Remove» each. Every item is the
 * person's own text, drawn as text.
 *
 * Bounded by the platform's own limits: at most `maxItems` items, each at most `maxLength`
 * characters; a duplicate is not added twice.
 */

import { Plus, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, FieldShell, FOCUS_RING, IconButton } from '../../primitives';

export interface ListFieldProps {
  readonly labelKey: string;
  readonly helpKey?: string;
  readonly errorKey?: string;
  readonly required?: boolean;
  readonly items: readonly string[];
  readonly onChange: (items: readonly string[]) => void;
  readonly maxItems: number;
  readonly maxLength?: number;
  /** Upper-cases what is typed — for market codes. */
  readonly upperCase?: boolean;
  /** Drawn beside «Add»: a quick way to add one item (the run on screen). */
  readonly extra?: React.ReactNode;
  /** For the tour. */
  readonly tourAnchor?: string;
}

const INPUT = [
  'bg-surface-raised text-ink-primary border border-hairline rounded-control h-8 px-2 text-body font-ui grow min-w-0',
  'aria-[invalid=true]:border-status-critical',
  FOCUS_RING,
].join(' ');

export function ListField({
  labelKey, helpKey, errorKey, required, items, onChange, maxItems, maxLength, upperCase = false, extra, tourAnchor,
}: ListFieldProps): React.JSX.Element {
  const { t } = useTranslation();
  const [draft, setDraft] = useState('');
  const full = items.length >= maxItems;

  const add = (): void => {
    const text = (upperCase ? draft.toUpperCase() : draft).trim();
    if (text === '' || full || items.includes(text)) return;
    onChange([...items, text]);
    setDraft('');
  };

  return (
    <FieldShell
      labelKey={labelKey}
      {...(helpKey === undefined ? {} : { helpKey })}
      {...(errorKey === undefined ? {} : { errorKey })}
      required={required === true}
    >
      {({ controlId, describedBy, invalid }) => (
        <div className="flex flex-col gap-2" {...(tourAnchor === undefined ? {} : { 'data-tour': tourAnchor })}>
          {items.length > 0 && (
            <ol className="flex flex-col gap-1">
              {items.map((item, index) => (
                <li key={item} className="border-hairline flex items-start gap-2 rounded-control border px-2 py-1">
                  <span className="text-body text-ink-primary grow break-words whitespace-pre-wrap" translate="no">{item}</span>
                  <IconButton
                    variant="ghost"
                    size="sm"
                    icon={<X size={12} aria-hidden />}
                    aria-label={t('feedback.list.remove', { item: index + 1 })}
                    onClick={() => onChange(items.filter((_, at) => at !== index))}
                  />
                </li>
              ))}
            </ol>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <input
              id={controlId}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              value={draft}
              disabled={full}
              {...(maxLength === undefined ? {} : { maxLength })}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key !== 'Enter' || event.nativeEvent.isComposing) return;
                event.preventDefault();
                add();
              }}
              className={INPUT}
            />
            <Button variant="secondary" size="sm" icon={<Plus size={12} aria-hidden />} onClick={add} disabled={full || draft.trim() === ''}>
              {t('feedback.list.add')}
            </Button>
            {extra}
          </div>
          {full && <p className="text-caption text-ink-secondary">{t('feedback.list.full', { count: maxItems })}</p>}
        </div>
      )}
    </FieldShell>
  );
}
